import Cookies from 'js-cookie';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

const CLARITY_SCRIPT_BASE = 'https://www.clarity.ms/tag/';
const IDLE_TIMEOUT_MS = 5000;
const CONSENT_COOKIE = 'rio-cfs';

let clarityLoaded = false;
let clarityLoading = false;

function loadClarityScript(clarityCode: string) {
  if (clarityLoaded || clarityLoading || !clarityCode) {
    return;
  }
  clarityLoading = true;

  // Set up the clarity function queue (same as original inline script)
  const win = window as any;
  if (!win.clarity) {
    win.clarity = (...args: any[]) => {
      (win.clarity.q = win.clarity.q || []).push(args);
    };
  }

  // Inject the script
  const script = document.createElement('script');
  script.src = CLARITY_SCRIPT_BASE + clarityCode;
  script.async = true;
  script.onload = () => {
    clarityLoaded = true;
  };
  document.head.appendChild(script);
}

function scheduleClarityLoad(clarityCode: string) {
  const supportsIdleCallback = 'requestIdleCallback' in window;

  if (supportsIdleCallback) {
    window.requestIdleCallback(() => loadClarityScript(clarityCode), { timeout: IDLE_TIMEOUT_MS });
  } else {
    setTimeout(() => loadClarityScript(clarityCode), IDLE_TIMEOUT_MS);
  }
}

export function ClarityLoader() {
  const clarityCode = useSelector((state: any) => state.siteConfig?.microsoftClarityCode);

  useEffect(() => {
    if (typeof window === 'undefined' || clarityLoaded || !clarityCode) {
      return;
    }

    // Only load Clarity if user has accepted cookies
    if (Cookies.get(CONSENT_COOKIE) === 'accepted') {
      scheduleClarityLoad(clarityCode);
      return;
    }

    // If no consent yet, listen for the consent-accepted event
    const onConsentAccepted = () => {
      scheduleClarityLoad(clarityCode);
    };

    window.addEventListener('rio:consent-accepted', onConsentAccepted);

    return () => {
      window.removeEventListener('rio:consent-accepted', onConsentAccepted);
    };
  }, [clarityCode]);

  return null;
}
